// src/pages/Payroll.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import "../css/Payroll.css";
import { jsPDF } from "jspdf";
import { change_employee_hourly_rate, get_employees_hours_for_month } from '../../utils/employeeRequests';
import { PaysPopup } from '../../components/paysPopup';
import { OBPopup } from '../../components/OBPopup';
import { TimeReportPopup } from '../../components/TimeReportPopup';
import { PayInfoPopup } from '../../components/PayInfoPopup';
import { OBUpdateOverlay } from '../../components/OBUpdateOverlay';
import { BACKEND_URL } from '../../constants';
import { Toast } from '../../components/ToastPopup';
import VacationPayPopup from '../../components/VacationPayPopup';
import OvertimePopup from '../../components/OvertimePopup';
import axios from 'axios';
import { AbsencePopup } from '../../components/AbsencePopup';
import { calculatePayForEmployee } from '../../utils/calculatePayForEmployee';
import PayrollExportPanel from '../../components/PayrollExportPanel';
import EmployeePayVisibility from '../../components/employeepayvisibility';

import { getPermission } from '../../permissions/rolePermissions';


const Payroll = () => {
  const {
    employees, setEmployees,
    usersHours, company, user,
    pays, setPays,
    draftSchedule, setDraftSchedule,
    obs, overtimeRules, vacationPay,
    timeSchemas, absenceRules, obUpdateInfo
  } = useAuth();

  const payrollPermission = getPermission(user?.role, "payroll");
  const isViewOnly = payrollPermission !== "edit";


  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allUsersHours, setAllUsersHours] = useState(usersHours || []);
  const [allPays, setAllPays] = useState(pays || []);
  const [allEmployees, setAllEmployees] = useState(employees || []);
  const [allOvertimeRules, setAllOvertimeRules] = useState(overtimeRules || []);
  const [allAbsenceRules, setAllAbsenceRules] = useState(absenceRules || []);
  const [allTimeSchemas, setAllTimeSchemas] = useState(timeSchemas || []);
  const [selectedTimeSchema, setSelectedTimeSchema] = useState(null);

  // Popup-states
  const [showObPopup, setShowObPopup] = useState(false);
  const [showPayPopup, setShowPayPopup] = useState(false);
  const [showTimeReportPopup, setShowTimeReportPopup] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVacationPayPopup, setShowVacationPayPopup] = useState(false);
  const [showOvertimePopup, setShowOvertimePopup] = useState(false);
  const [showPayInfoPopup, setShowPayInfoPopup] = useState(false);
  const [showAbsencePopup, setShowAbsencePopup] = useState(false);
  // Lägg till detta med de andra state-deklarationerna
const [showPayVisibilityPopup, setShowPayVisibilityPopup] = useState(false);


  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const isFetchingRef = useRef(false); // prevent overlapping fetches

  const monthsInSwedish = [
    'Januari','Februari','Mars','April','Maj','Juni',
    'Juli','Augusti','September','Oktober','November','December'
  ];

  // Export-panel
  const [showExport, setShowExport] = useState(false);
  const [payrollSystem, setPayrollSystem] = useState(company?.payrollSystem ?? "");
  const period = {
    from: `${selectedYear}-${String(selectedMonth).padStart(2,"0")}-01`,
    to:   `${selectedYear}-${String(selectedMonth).padStart(2,"0")}-30`
  };

  // Mapping (kan sparas per-företag i backend)
  const mapping = useMemo(() => ({
    base: company?.payrollDefaults?.baseTimeSalaryCode || "",
    ob: (obs || []).map(r => ({ _id: r._id, name: r.name, code: r.salaryCode || "" })),
    ot: (overtimeRules || []).map(r => ({ _id: r._id, name: r.name, code: r.salaryCode || "" })),
    absence: (absenceRules || []).map(r => ({ _id: r._id, name: r.name, code: r.salaryCode || "" })),
    vacation: vacationPay?.salaryCode ?? ""
  }), [obs, overtimeRules, absenceRules, vacationPay, company]);

  const [mappingState, setMappingState] = useState(mapping);
  useEffect(() => setMappingState(mapping), [mapping]);

  // Hjälp: är ett pass komplett?
  const isShiftCompleteForPayroll = (shift) => {
    return !!(
      shift &&
      shift.status === "complete" &&
      shift.date &&
      typeof shift.startTime === "string" && shift.startTime.includes(":") &&
      typeof shift.endTime === "string" && shift.endTime.includes(":") &&
      Array.isArray(shift.employees) &&
      shift.employees.some(e => !!e?.employeeId)
    );
  };




  // Filtrera anställda som faktiskt har skift i vald vy
  const employeesWithShiftsInSchema = useMemo(() => {
    if (!selectedTimeSchema) return allEmployees;

    return allEmployees.filter(emp =>
      draftSchedule.some(shift => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const d = new Date(shift.date);
        const isCorrectMonth = d.getMonth() + 1 === selectedMonth;
        const isCorrectYear  = d.getFullYear() === selectedYear;
        const matchesSchema  = shift.timeSchema === selectedTimeSchema;
        const hasEmployee    = shift.employees.some(e => e.employeeId === emp._id);
        return isCorrectMonth && isCorrectYear && matchesSchema && hasEmployee;
      })
    );
  }, [allEmployees, draftSchedule, selectedTimeSchema, selectedMonth, selectedYear]);

  // Sökfilter
  const filteredData = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    return employeesWithShiftsInSchema.filter(emp =>
      (emp.name || "").toLowerCase().includes(q) ||
      (emp.email || "").toLowerCase().includes(q) ||
      (emp.role || "").toLowerCase().includes(q)
    );
  }, [employeesWithShiftsInSchema, searchQuery]);



  const sortBreakdownItems = (type, items) => {
    if (!Array.isArray(items)) return [];

    // Only enforce ordering for OB breakdown
    if (type !== "ob") return items;

    // Build priority lookup (lower number = lower priority)
    const prioById = {};
    (obs || []).forEach((o) => {
      prioById[String(o._id)] = Number(o.priority ?? 0);
    });

    // Stable sort: priority asc, then name asc
    return items.slice().sort((a, b) => {
      const ap = prioById[String(a._id)] ?? prioById[String(a.obType)] ?? 0;
      const bp = prioById[String(b._id)] ?? prioById[String(b.obType)] ?? 0;
      if (ap !== bp) return bp - ap;

      const an = (a.name || "").toString();
      const bn = (b.name || "").toString();
      return an.localeCompare(bn, "sv-SE");
    });
  };

  // Summeringar
  const totals = useMemo(() => {
    const hoursSum = filteredData.reduce((sum, emp) => {
      const empShifts = draftSchedule.filter(shift => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const d = new Date(shift.date);
        const matchesMonth    = d.getMonth() + 1 === selectedMonth;
        const matchesYear     = d.getFullYear() === selectedYear;
        const matchesSchema   = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
        const matchesEmployee = shift.employees.some(e => e.employeeId === emp._id);
        return matchesMonth && matchesYear && matchesSchema && matchesEmployee;
      });

      const { totalHours } = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates: allPays,
        vacationPay,
        overtimeRules: allOvertimeRules,
        obRules: obs,
        absenceRules: allAbsenceRules
      });

      return sum + (totalHours || 0);
    }, 0);

    return {
      employeesCount: filteredData.length,
      hours: hoursSum,
    };
  }, [
    filteredData,
    draftSchedule,
    selectedMonth,
    selectedYear,
    selectedTimeSchema,
    allPays,
    vacationPay,
    allOvertimeRules,
    obs,
    allAbsenceRules
  ]);


  {showPayInfoPopup && (
  <PayInfoPopup onClose={() => setShowPayInfoPopup(false)} />
)}

{showPayVisibilityPopup && (
  <EmployeePayVisibility onClose={() => setShowPayVisibilityPopup(false)} />
)}

<PayrollExportPanel
  isOpen={showExport}
  onClose={() => setShowExport(false)}
  period={period}
  payrollSystem={payrollSystem}
  setPayrollSystem={setPayrollSystem}
  totals={totals}
  mapping={mappingState}
  setMapping={setMappingState}
  onGeneratePaxml={handleGeneratePaxml}
  employees={filteredData}
  draftSchedule={draftSchedule}
  selectedMonth={selectedMonth}
  selectedYear={selectedYear}
  selectedTimeSchema={selectedTimeSchema}
  payRates={allPays}
  vacationPay={vacationPay}
  overtimeRules={allOvertimeRules}
  obRules={obs}
  absenceRules={allAbsenceRules}
/>


  // Per-anställd summering (timmar) för exportpanelen steg 1
  const employeeSummaries = useMemo(() => {
    return filteredData.map(emp => {
      const empShifts = draftSchedule.filter(shift => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const d = new Date(shift.date);
        const matchesMonth    = d.getMonth() + 1 === selectedMonth;
        const matchesYear     = d.getFullYear() === selectedYear;
        const matchesSchema   = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
        const matchesEmployee = shift.employees.some(e => e.employeeId === emp._id);
        return matchesMonth && matchesYear && matchesSchema && matchesEmployee;
      });

      const { totalHours } = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates: allPays,
        vacationPay,
        overtimeRules: allOvertimeRules,
        obRules: obs,
        absenceRules: allAbsenceRules
      });

      return { id: emp._id, name: emp.name, hours: totalHours || 0 };
    });
  }, [
    filteredData,
    draftSchedule,
    selectedMonth,
    selectedYear,
    selectedTimeSchema,
    allPays,
    vacationPay,
    allOvertimeRules,
    obs,
    allAbsenceRules
  ]);

  // ---------- Export handler ----------
  async function handleGeneratePaxml() {
    try {
      const systemToFormat = {
        fortnox: "paxml",
        kontek: "paxml",
        bl_lon: "paxml",
        crona: "paxml",
        visma_tlu: "tlu",
      };
      const format = systemToFormat[payrollSystem] || "paxml";

      const employeeIds = filteredData.map(e => e._id);

      const body = {
        userId: user._id,
        companyId: company._id,
        format,
        payrollSystem,
        period,
        selectedMonth,
        selectedYear,
        selectedTimeSchema,
        employeeIds,
        mapping: mappingState,
        client: { app: "TimeSmart", feature: "payroll-export", version: 1 },
      };

      const res = await axios.post(
        `${BACKEND_URL}/payroll/export-paxml`,
        body,
        { responseType: "blob" }
      );

      const ext = format === "tlu" ? "tlu" : "xml";
      const filename = `${payrollSystem}_${period.from}_${period.to}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setShowExport(false);
      setToastType("success");
      setToastMessage(`Export klar – filen ${filename} har laddats ned.`);
    } catch (err) {
      console.error("Export error:", err);
      setToastType("error");
      setToastMessage("Kunde inte generera exportfil. Försök igen eller kontakta support.");
    }
  }

  // === Datahämtning och reopen ===
  useEffect(() => {
    const reopen = localStorage.getItem("reopenTimeReportPopup");
    if (!reopen) return;

    const { employeeId, selectedMonth: storedMonth, selectedYear: storedYear } = JSON.parse(reopen);

    const monthHasShifts = draftSchedule.some(s => {
      const d = new Date(s.date);
      return d.getMonth() + 1 === storedMonth && d.getFullYear() === storedYear;
    });

    const fetchAndOpenPopup = async () => {
      if (!monthHasShifts && !isFetchingRef.current) {
        isFetchingRef.current = true;
        setPayLoading(true);
        try {
          const { data } = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
            params: { userId: user._id, companyId: company._id, selectedMonth: storedMonth, selectedYear: storedYear }
          });
          setDraftSchedule(prev => [
            ...prev.filter(s => {
              const d = new Date(s.date);
              return !(d.getMonth() + 1 === storedMonth && d.getFullYear() === storedYear);
            }),
            ...(data.shifts || [])
          ]);
        } catch (err) {
          console.error("Failed to fetch shifts for reopen:", err);
        } finally {
          setPayLoading(false);
          isFetchingRef.current = false;
        }
      }

      setSelectedMonth(storedMonth);
      setSelectedYear(storedYear);

      const emp = employees.find(e => e._id === employeeId);
      if (emp) {
        setSelectedEmployee(emp);
        setShowTimeReportPopup(true);
      }
      localStorage.removeItem("reopenTimeReportPopup");
    };

    fetchAndOpenPopup();
  }, [employees, user._id, company._id]);

  // Hämta skift när månad/år ändras (utan att loopa)
  useEffect(() => {
    const hasMonthData = draftSchedule.some(shift => {
      const d = new Date(shift.date);
      return d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear;
    });
    if (hasMonthData) return;

    const fetchShifts = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setPayLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
          params: { userId: user._id, companyId: company._id, selectedMonth, selectedYear }
        });
        setDraftSchedule(prev => {
          const filteredPrev = prev.filter(s => {
            const d = new Date(s.date);
            return !(d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear);
          });
          return [...filteredPrev, ...(data.shifts || [])];
        });
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
      } finally {
        setPayLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchShifts();
  }, [selectedMonth, selectedYear, user._id, company._id, setDraftSchedule]);

  const updateAllPays = ({ updatedPays }) => {
    setAllPays(updatedPays);
    setPays(updatedPays);
  };

  const updatedraftSchedule = ({ updatedShifts }) => {
    setDraftSchedule(updatedShifts);
  };

  const getShiftsForEmployeeThisMonth = (employeeId) => {
    return draftSchedule.filter(shift => {
      if (!isShiftCompleteForPayroll(shift)) return false;
      const d = new Date(shift.date);
      const matchesMonth    = d.getMonth() + 1 === selectedMonth;
      const matchesYear     = d.getFullYear() === selectedYear;
      const matchesEmployee = shift.employees.some(e => e.employeeId === employeeId);
      const matchesSchema   = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
      return matchesMonth && matchesYear && matchesEmployee && matchesSchema;
    });
  };

  const handleMonthChange = e => setSelectedMonth(parseInt(e.target.value));
  const handleYearChange  = e => setSelectedYear(parseInt(e.target.value));

  const changeEmployeeHourlyPay = async ({ employeeId, newPayId }) => {

    if (isViewOnly) return;

    const { data } = await axios.put(`${BACKEND_URL}/change-users-hourly-pay`, {
      employeeId, newPayId, userId: user._id, companyId: company._id
    });
    if (data.status !== "success") {
      setToastType("error");
      setToastMessage(data.errorText || "Kunde inte ändra lön, kontakta support!");
      return;
    }
    setEmployees(prev => prev.map(emp => emp._id === employeeId ? { ...emp, pay: newPayId } : emp));
    setAllEmployees(prev => prev.map(emp => emp._id === employeeId ? { ...emp, pay: newPayId } : emp));
    setToastType("success");
    setToastMessage("Lön ändrad");
  };

  const handleEmployeePopup = emp => {
    setSelectedEmployee(emp);
    setShowTimeReportPopup(true);
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const Spinner = () => (
    <div className="spinner-container">
      <div className="spinner" />
    </div>
  );

  // ─────────────────────────────────────────────
  // PER-ANSTÄLLD DUMMY-SETTINGS (OB / ÖT / Sem / Skatt)
  // ─────────────────────────────────────────────
  const [employeeSettings, setEmployeeSettings] = useState({});
  // { [employeeId]: { includeOB, includeOvertime, includeVacation, taxRate } }

  useEffect(() => {
    setEmployeeSettings(prev => {
      const next = { ...prev };

      allEmployees.forEach(emp => {
        const serverSettings = emp.payrollSettings || {};

        if (!next[emp._id]) {
          next[emp._id] = {
            includeOB: serverSettings.includeOB ?? true,
            includeOvertime: serverSettings.includeOvertime ?? true,
            includeVacation: serverSettings.includeVacation ?? true,
            taxRate: typeof serverSettings.taxRate === "number" ? serverSettings.taxRate : 30,
          };
        }
      });

      Object.keys(next).forEach(id => {
        if (!allEmployees.find(e => e._id === id)) {
          delete next[id];
        }
      });

      return next;
    });
  }, [allEmployees]);

  const updateEmployeeSetting = async (id, patch) => {
    if (isViewOnly) return;
    setEmployeeSettings(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {
          includeOB: true,
          includeOvertime: true,
          includeVacation: true,
          taxRate: 30,
        }),
        ...patch,
      },
    }));

    try {
      const payload = {
        userId: user._id,
        companyId: company._id,
        employeeId: id,
      };

      if (Object.prototype.hasOwnProperty.call(patch, "includeOB")) {
        payload.includeOB = patch.includeOB;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "includeVacation")) {
        payload.includeVacation = patch.includeVacation;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "taxRate")) {
        payload.taxRate = patch.taxRate;
      }

      const { data } = await axios.put(
        `${BACKEND_URL}/payroll/update-employee-settings`,
        payload
      );

      if (data.status !== "success") {
        setToastType("error");
        setToastMessage(data.errorText || "Kunde inte spara inställningar, kontakta support!");
        return;
      }

      const serverSettings = data.payrollSettings || data.updatedUser?.payrollSettings;

      if (data.updatedUser) {
        setEmployees(prev =>
          prev.map(emp =>
            emp._id === id ? { ...emp, payrollSettings: serverSettings } : emp
          )
        );
        setAllEmployees(prev =>
          prev.map(emp =>
            emp._id === id ? { ...emp, payrollSettings: serverSettings } : emp
          )
        );
      }

      if (serverSettings) {
        setEmployeeSettings(prev => ({
          ...prev,
          [id]: {
            ...(prev[id] || {
              includeOB: true,
              includeOvertime: true,
              includeVacation: true,
              taxRate: 30,
            }),
            includeOB:
              typeof serverSettings.includeOB === "boolean"
                ? serverSettings.includeOB
                : (prev[id]?.includeOB ?? true),
            includeVacation:
              typeof serverSettings.includeVacation === "boolean"
                ? serverSettings.includeVacation
                : (prev[id]?.includeVacation ?? true),
            taxRate:
              typeof serverSettings.taxRate === "number"
                ? serverSettings.taxRate
                : (prev[id]?.taxRate ?? 30),
          },
        }));
      }

      setToastType("success");
      setToastMessage("Inställningar uppdaterade");
    } catch (err) {
      console.error("updateEmployeeSetting error:", err);
      setToastType("error");
      setToastMessage("Kunde inte spara inställningar, försök igen.");
    }
  };

  const formatHours = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0,00";
    return n.toFixed(2);
  };

  const formatCurrency = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0 kr";
    return n.toLocaleString("sv-SE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " kr";
  };

  // ─────────────────────────────────────────────
  // HOVER BREAKDOWN POPOVER (STRIKT: 500ms på samma chip)
  // ─────────────────────────────────────────────
  const [breakdownPopup, setBreakdownPopup] = useState(null);
  // { type, employeeId, mouseX, mouseY, items, sourceKey }

  const openTimerRef = useRef(null);
  const hoverSourceRef = useRef(null); // { sourceKey }

  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const closePopoverImmediately = () => {
    clearOpenTimer();
    setBreakdownPopup(null);
    hoverSourceRef.current = null;
  };

  // Visa inte popover om allt är 0 / tomt
  const hasMeaningfulItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.some(it => {
      const pay = Number(it?.pay ?? 0);
      const h1 = Number(it?.hours ?? 0);
      const h2 = Number(it?.overtimeHours ?? 0);
      return pay > 0 || h1 > 0 || h2 > 0;
    });
  };

  // stäng/avbryt om man skrollar / wheels / touchmove
  useEffect(() => {
    const onScrollIntent = () => {
      closePopoverImmediately();
    };

    window.addEventListener("scroll", onScrollIntent, true);
    window.addEventListener("wheel", onScrollIntent, { passive: true });
    window.addEventListener("touchmove", onScrollIntent, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScrollIntent, true);
      window.removeEventListener("wheel", onScrollIntent);
      window.removeEventListener("touchmove", onScrollIntent);
    };
  }, []);

  const openBreakdownPopoverDelayed = (type, e, items, employeeId, sourceKey) => {
    clearOpenTimer();

    const sortedItems = sortBreakdownItems(type, items);

    if (!hasMeaningfulItems(sortedItems)) {
      closePopoverImmediately();
      return;
    }

    hoverSourceRef.current = { sourceKey };

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    openTimerRef.current = setTimeout(() => {
      if (hoverSourceRef.current?.sourceKey !== sourceKey) return;

      setBreakdownPopup({
        type,
        employeeId,
        mouseX,
        mouseY,
        items: sortedItems,
        sourceKey,
      });
    }, 700);
  };


  const handleChipLeave = (sourceKey) => {
    // lämnar man en chip: avbryt öppning + stäng om just den chipen var öppen
    if (hoverSourceRef.current?.sourceKey === sourceKey) {
      hoverSourceRef.current = null;
      clearOpenTimer();
    }
    if (breakdownPopup?.sourceKey === sourceKey) {
      setBreakdownPopup(null);
    }
  };

  return (
    <div className="payroll-page">
      <Nav />
      <div className="payroll-container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 700, color: "#2d2d2d", margin: 0 }}>Lönhantering</h1>

            {/* Info-ikon */}
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                onClick={() => setShowPayInfoPopup(true)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, margin: 0 }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                
              >
                <span style={{
                  display: "inline-flex",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#e0e7ff",
                  color: "#3b4cca",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: "14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)"
                }}>i</span>
              </button>

              {showTooltip && (
                <div style={{
                  position: "absolute",
                  top: "28px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#f4f4f4",
                  color: "#555",
                  padding: "4px 8px",
                  fontSize: "10px",
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  zIndex: 10,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  opacity: 0.95
                }}>
                  Visa hur lönen räknas
                </div>
              )}
            </div>
          </div>
        </div>



        <div className="top-inputs-container">
          <div className="search-bar-container">
            <input
              type="text"
              placeholder="Sök namn, email, roll..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
              
            />
          </div>

          <select
            className="month-select"
            style={{ fontSize: "13px", padding: "6px 12px", borderRadius: "6px", border: "1px solid #ccc", backgroundColor: "#fff" }}
            value={selectedTimeSchema || ""}
            onChange={e => setSelectedTimeSchema(e.target.value)}
          >
            <option value="">Alla scheman</option>
            {allTimeSchemas.map((timeSchema) => (
              <option key={timeSchema._id} value={timeSchema._id}>{timeSchema.name}</option>
            ))}
          </select>




          <select className="month-select payroll-select" value={selectedMonth} onChange={handleMonthChange}>
            {monthsInSwedish.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>

          <select className="year-select payroll-select" value={selectedYear} onChange={handleYearChange}>
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + 1 - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

<div style={{ display: "flex", alignItems: "center", gap: "0px", marginLeft: "0px", padding: "2px" }}>



            <button
              className="payroll-button"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setShowPayPopup(true)}
            >
              Löner
            </button>

            <button
              className="payroll-button"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setShowObPopup(true)}
            >
              OB
            </button>

            <button
              className="payroll-button"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "110px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setShowOvertimePopup(true)}
            >
              Övertid
            </button>

            <button
              className="payroll-button"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "160px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setShowVacationPayPopup(true)}
            >
              Semesterersättning
            </button>

            <button
              className="payroll-button"
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                fontSize: "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                minWidth: "110px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setShowAbsencePopup(true)}
            >
              Frånvaro
            </button>



{/* <button
  className="payroll-button"
  style={{
    padding: "8px 16px",
    borderRadius: "6px",
    border: "1px solid #007bff",
    backgroundColor: "#007bff",
    color: "white",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "130px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px"
  }}
  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
  onClick={() => setShowPayVisibilityPopup(true)}
>
  Lönesynlighet
</button> */}

            
            
          </div>
        </div>

        <div className="payroll-grid">
          <div className="payroll-header">
            <span>Namn / kontakt</span>
            <span>Lön</span>
            <span>Timmar (sammanställning)</span>
            <span>Lönesammanställning</span>
            <span>Inställningar & skatt</span>
          </div>

          {payLoading ? (
            <Spinner />
          ) : (
            filteredData.map(employee => {
              const empShifts = draftSchedule.filter(shift => {
                if (!isShiftCompleteForPayroll(shift)) return false;
                const d = new Date(shift.date);
                const matchesMonth    = d.getMonth() + 1 === selectedMonth;
                const matchesYear     = d.getFullYear() === selectedYear;
                const matchesSchema   = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
                const matchesEmployee = shift.employees.some(e => e.employeeId === employee._id);
                return matchesMonth && matchesYear && matchesSchema && matchesEmployee;
              });

              const { summary } = calculatePayForEmployee({
                employee,
                shifts: empShifts,
                payRates: allPays,
                vacationPay,
                overtimeRules: allOvertimeRules,
                obRules: obs,
                absenceRules: allAbsenceRules,
                returnDetailed: true
              });

              const {
                totalHours,
                totalPay,
                obPay,
                vacationCompensation,
                overTimePay,
                baseHours,
                overtimeHours,
                absenceHours,
                obBreakdown,
                overtimeBreakdown,
                absenceBreakdown,
              } = summary;

              const currentPayExists = allPays.some(p => p._id === employee.pay);
              const settings = employeeSettings[employee._id] || {
                includeOB: true,
                includeOvertime: true,
                includeVacation: true,
                taxRate: 30,
              };

              const hasBreakdown =
                baseHours != null ||
                overtimeHours != null ||
                absenceHours != null;

              const selectedPayObj = allPays.find(p => p._id === employee.pay);
              const payTypeLabel = selectedPayObj
                ? selectedPayObj.payType === "monthly"
                  ? "Månadslön"
                  : "Timlön"
                : "Ingen lön vald";

              // unika nycklar per chip
              const otHoursKey = `hours-ot-${employee._id}`;
              const absHoursKey = `hours-abs-${employee._id}`;
              const otPayKey = `pay-ot-${employee._id}`;
              const obPayKey = `pay-ob-${employee._id}`;

              return (
                <div
                  className="payroll-row"
                  key={employee._id}
                  onClick={() => handleEmployeePopup(employee)}
                >
                  {/* Namn + email + roll/titel */}
                  <span className="name-cell">
                    <div className="name-main">
                      {employee.name || "Okänd användare"}
                    </div>
                    {employee.email && (
                      <div className="name-sub">
                        {employee.email}
                      </div>
                    )}
                    {(employee.role || employee.title) && (
                      <div className="name-sub-muted">
                        {employee.role || employee.title}
                      </div>
                    )}
                  </span>

                  {/* Lön */}
                  <span className="pay-cell" onClick={e => e.stopPropagation()}>
                    <div className="pay-type-chip">
                      {payTypeLabel}
                    </div>
                    <div className="pay-select-wrapper">
                      <select
                        value={currentPayExists ? employee.pay : ""}
                        onChange={e => changeEmployeeHourlyPay({
                          employeeId: employee._id,
                          newPayId: e.target.value
                        })}
                        disabled={isViewOnly}
                        style={{fontSize: 11, minWidth: 140, maxWidth: 200, padding: 4, border: "1px solid #cccccc", borderRadius: 4, cursor: "pointer"}}
                      >
                        <option value="">(0 kr/h) ingen lön vald</option>
                        {allPays.map(pay => (
                          <option key={pay._id} value={pay._id}>
                            ({pay.amount} {pay.payType === "monthly" ? "kr/mån" : "kr/h"}) {pay.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </span>

                  {/* Timmar – hover bara på Övertid/Frånvaro chips */}
                  <span className="hours-cell" onClick={e => e.stopPropagation()}>
                    <div className="hours-summary">
                      <div className="hours-chip">
                        <span className="hours-chip-label">Ordinarie</span>
                        <span className="hours-chip-value">
                          {formatHours(baseHours ?? 0)} h
                        </span>
                      </div>

                      <div
                        className="hours-chip"
                        onMouseEnter={(e) =>
                          openBreakdownPopoverDelayed("ot", e, overtimeBreakdown, employee._id, otHoursKey)
                        }
                        onMouseLeave={() => handleChipLeave(otHoursKey)}
                        style={{ cursor: "default" }}
                      >
                        <span className="hours-chip-label">Övertid</span>
                        <span className="hours-chip-value">
                          {formatHours(hasBreakdown ? (overtimeHours ?? 0) : 0)} h
                        </span>
                      </div>

                      <div
                        className="hours-chip"
                        onMouseEnter={(e) =>
                          openBreakdownPopoverDelayed("absence", e, absenceBreakdown, employee._id, absHoursKey)
                        }
                        onMouseLeave={() => handleChipLeave(absHoursKey)}
                        style={{ cursor: "default" }}
                      >
                        <span className="hours-chip-label">Frånvaro</span>
                        <span className="hours-chip-value">
                          {formatHours(hasBreakdown ? (absenceHours ?? 0) : 0)} h
                        </span>
                      </div>
                    </div>

                    <div className="hours-total-chip">
                      <span>Totalt timmar</span>
                      <span className="hours-total-value">{formatHours(totalHours)} h</span>
                    </div>
                  </span>

                  {/* Lönedelarna – hover bara på ÖT-pengar + OB-pengar */}
                  <span className="salary-breakdown-cell" onClick={e => e.stopPropagation()}>
                    <div className="salary-summary">
                      <div
                        className="salary-chip"
                        onMouseEnter={(e) =>
                          openBreakdownPopoverDelayed("ot", e, overtimeBreakdown, employee._id, otPayKey)
                        }
                        onMouseLeave={() => handleChipLeave(otPayKey)}
                        style={{ cursor: "default" }}
                      >
                        <div className="salary-chip-label">ÖT-tillägg</div>
                        <div className="salary-chip-value">{formatCurrency(overTimePay)}</div>
                      </div>

                      <div
                        className="salary-chip"
                        onMouseEnter={(e) =>
                          openBreakdownPopoverDelayed("ob", e, obBreakdown, employee._id, obPayKey)
                        }
                        onMouseLeave={() => handleChipLeave(obPayKey)}
                        style={{ cursor: "default" }}
                      >
                        <div className="salary-chip-label">OB-tillägg</div>
                        <div className="salary-chip-value">{formatCurrency(obPay)}</div>
                      </div>

                      <div className="salary-chip">
                        <div className="salary-chip-label">Semesterersättning (12%)</div>
                        <div className="salary-chip-value">{formatCurrency(vacationCompensation)}</div>
                      </div>
                    </div>

                    <div className="salary-total-chip">
                      <div className="salary-total-label">Total Lön (SEK)</div>
                      <div className="salary-total-value">{formatCurrency(totalPay)}</div>
                    </div>
                  </span>

                  {/* Inställningar & skatt */}
                  <span className="settings-cell" onClick={e => e.stopPropagation()}>
                    <div className="settings-grid">
                      <div className="settings-label">OB</div>
                      <select
                        value={settings.includeOB ? "on" : "off"}
                        onChange={e =>
                          updateEmployeeSetting(employee._id, {
                            includeOB: e.target.value === "on",
                          })
                        }
                        disabled={isViewOnly}
                        className="settings-select"
                      >
                        <option value="on">På</option>
                        <option value="off">Av</option>
                      </select>

                      <div className="settings-label">Sem</div>
                      <select
                        value={settings.includeVacation ? "on" : "off"}
                        onChange={e =>
                          updateEmployeeSetting(employee._id, {
                            includeVacation: e.target.value === "on",
                          })
                        }
                        disabled={isViewOnly}
                        className="settings-select"
                      >
                        <option value="on">På</option>
                        <option value="off">Av</option>
                      </select>

                      <div className="settings-label">Skatt %</div>
                      <input
                        type="number"
                        value={settings.taxRate}
                        onChange={e =>
                          updateEmployeeSetting(employee._id, {
                            taxRate: Number(e.target.value) || 0,
                          })
                        }
                        disabled={isViewOnly}
                        className="settings-input"
                        min="0"
                        max="100"
                      />
                    </div>
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* POPOVER: visas där musen var efter intent, stannar där tills chip lämnas */}
        {breakdownPopup && (
          (() => {
            const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
            const vh = typeof window !== "undefined" ? window.innerHeight : 800;
            const approxW = 240;
            const approxH = 210;
            const pad = 10;

            let left = (breakdownPopup.mouseX ?? 0) + 12;
            let top = (breakdownPopup.mouseY ?? 0) + 6;

            if (left + approxW + pad > vw) left = vw - approxW - pad;
            if (top + approxH + pad > vh) top = vh - approxH - pad;
            if (left < pad) left = pad;
            if (top < pad) top = pad;

            return (
              <div
                className="mini-breakdown-popover"
                style={{
                  position: "fixed",
                  top,
                  left,
                  zIndex: 9999,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                  padding: "8px 10px",
                  minWidth: 190,
                  maxWidth: 240,
                  fontSize: 10,
                  color: "#111827",
                  pointerEvents: "none", // 🔒 popover ska INTE fånga hover — bara chip styr allt
                  lineHeight: 1.25,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6 }}>
                  {breakdownPopup.type === "ob"
                    ? "OB"
                    : breakdownPopup.type === "ot"
                    ? "Övertid"
                    : breakdownPopup.type === "absence"
                    ? "Frånvaro"
                    : "Detaljer"}
                </div>

                {(!breakdownPopup.items || breakdownPopup.items.length === 0) ? (
                  <div style={{ fontSize: 10, color: "#6b7280" }}>Ingen data</div>
                ) : (
                  breakdownPopup.items.map(it => (
                    <div
                      key={it._id || it.name}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 8,
                        alignItems: "center",
                        padding: "3px 0",
                        borderBottom: "1px dashed #f3f4f6",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{it.name}</div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {"hours" in it && Number(it.hours) > 0 && (
                          <div style={{ color: "#374151", fontWeight: 500 }}>
                            {formatHours(it.hours)} h
                          </div>
                        )}
                        {"overtimeHours" in it && Number(it.overtimeHours) > 0 && (
                          <div style={{ color: "#374151", fontWeight: 500 }}>
                            {formatHours(it.overtimeHours)} h
                          </div>
                        )}
                        {Number(it.pay) > 0 && (
                          <div style={{ fontWeight: 700 }}>
                            {formatCurrency(it.pay)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })()
        )}

        {showPayPopup && (
          <PaysPopup
            onClose={() => setShowPayPopup(false)}
            onUpdatePays={updateAllPays}
            setLoading={setLoading}
            readOnly={isViewOnly}
          />
        )}

        {(showObPopup || (obUpdateInfo.isUpdating && obUpdateInfo.status !== "done")) && (
          <OBPopup
            onClose={() => setShowObPopup(false)}
            onSetUpdateShifts={updatedraftSchedule}
            setLoading={setLoading}
            readOnly={isViewOnly}
          />
        )}

        {showVacationPayPopup && (
          <VacationPayPopup
            onClose={() => setShowVacationPayPopup(false)}
            setLoading={setLoading}
            readOnly={isViewOnly}
          />
        )}

        {showOvertimePopup && (
          <OvertimePopup
            onClose={() => setShowOvertimePopup(false)}
            company={company}
            user={user}
            setLoading={setLoading}
            readOnly={isViewOnly}
          />
        )}

        {showAbsencePopup && (
          <AbsencePopup 
            onClose={() => setShowAbsencePopup(false)}
            readOnly={isViewOnly}
          />
        )}

        {showTimeReportPopup && selectedEmployee && (
          <TimeReportPopup
            employee={selectedEmployee}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            selectedTimeSchema={selectedTimeSchema}
            shifts={draftSchedule}
            onClose={() => setShowTimeReportPopup(false)}
            readOnly={isViewOnly}
          />
        )}

        {showPayInfoPopup && (
          <PayInfoPopup onClose={() => setShowPayInfoPopup(false)} />
        )}

        {showPayVisibilityPopup && (
          <EmployeePayVisibility onClose={() => setShowPayVisibilityPopup(false)} />
        )}

        <PayrollExportPanel
          isOpen={showExport}
          onClose={() => setShowExport(false)}
          period={period}
          payrollSystem={payrollSystem}
          setPayrollSystem={setPayrollSystem}
          totals={totals}
          mapping={mappingState}
          setMapping={setMappingState}
          onGeneratePaxml={handleGeneratePaxml}
          employees={filteredData}
          draftSchedule={draftSchedule}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          selectedTimeSchema={selectedTimeSchema}
          payRates={allPays}
          vacationPay={vacationPay}
          overtimeRules={allOvertimeRules}
          obRules={obs}
          absenceRules={allAbsenceRules}
        />

        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage("")}
        />
      </div>
    </div>
  );
};

export default Payroll;
